import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { Avatar } from '../components/Avatar';
import { useToast } from '../components/Toast';
import { useLightbox } from '../components/Lightbox';
import { CREW, INTROS, crewPostsFor, crewMet, pendingMeet, type CrewId } from '../data/crew';
import { getChallenge } from '../data/exercises';
import { loadProfile, saveProfile, currentDay, recordMet } from '../lib/state';
import { listMyPosts, listFriends, listFriendsPosts, toggleHeart, deletePost, reportPost, setVisibility, hiddenPostIds, type FeedPost, type Friend } from '../lib/feed';
import { clearFeedNew } from '../lib/feed-flash';
import { isSaved, saveWithGoogle, saveWithEmail, warmGoogleSave } from '../lib/account';
import { inAppBrowser, inAppName } from '../lib/in-app-browser';
import { MoveToBrowser } from '../components/MoveToBrowser';
import { track } from '../analytics';
import { CrewFinds } from '../components/CrewFinds';
import { Post, type Slide } from '../components/Post';
import type { Work } from '../data/types';
import { heldPrints, withoutHeld } from '../lib/reveal';
import { useViewed } from '../lib/viewed';

type Item = { kind: 'mine'; post: FeedPost; at: number; held: number } | { kind: 'friend'; post: FeedPost & { byName?: string }; at: number } | { kind: 'crew'; id: string; by: CrewId; text: string; from?: string; exercise?: string; posted: 'intro' | 'note' | 'drawing' | 'quote'; day: number; at: number };

/**
 * The feed (10 §7, 07-feed-at-day-30.md): where your things are kept, in the
 * shape of a feed, for an audience of the people you chose. You, your crew's
 * script, and (from slice 3) friends. One heart, no number. Report and delete
 * on every post. The soft save is offered once, here, after the first card.
 */
export function Feed() {
  useViewed('feed');
  const nav = useNavigate();
  const [profile, setProfile] = useState(loadProfile);
  const day = currentDay(profile);
  const [posts, setPosts] = useState<FeedPost[] | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendPosts, setFriendPosts] = useState<(FeedPost & { byName?: string })[]>([]);
  const [hidden, setHidden] = useState(hiddenPostIds);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [now] = useState(() => Date.now());
  const { toast, show } = useToast();
  // you came to look: the tab's flash has done its job
  useEffect(() => { clearFeedNew(); }, []);
  const { open, lightbox } = useLightbox();
  const showSave = !isSaved() && !profile.saveDismissed && profile.madeDays > 0;
  // Inside Facebook's (or Instagram's) browser Google refuses to sign anyone in
  // (403 disallowed_useragent), so the save is email only there.
  const iab = inAppBrowser();
  useEffect(() => { if (showSave && !iab) warmGoogleSave(); }, [showSave, iab]);
  // Nobody is on the feed until they have knocked and been let in (owner's rule, 2026-09-18), Kraben included.
  const knock = pendingMeet(profile.madeDays, profile.met);
  const [knockLater, setKnockLater] = useState(false);
  const letIn = (id: CrewId) => { const p = recordMet(profile, id); setProfile(p); track('crew_met', { crew: id, day: p.madeDays }); show(`${CREW[id].name} is on your feed now.`); };

  const reload = () => { listMyPosts().then(setPosts).catch(() => setPosts([])); listFriends().then(async (fs) => { setFriends(fs); setFriendPosts(await listFriendsPosts(fs)); }).catch(() => {}); };
  useEffect(() => { reload(); track('feed_opened', { day }); }, [day]);

  const items = useMemo<Item[]>(() => {
    // your own pictures not yet turned over stay face-down here too (lib/reveal.ts)
    const own = (posts || []).filter((p) => !hidden.has(p.postId));
    const mine: Item[] = withoutHeld(own).map((p, i) => ({ kind: 'mine', post: p, at: p.createdAt, held: heldPrints([own[i]]).length }));
    const theirs: Item[] = friendPosts.filter((p) => !hidden.has(p.postId)).map((p) => ({ kind: 'friend', post: p, at: p.createdAt }));
    const startedAt = profile.startedAt || now;
    const crew: Item[] = crewPostsFor(profile.madeDays, profile.met).filter((c) => !hidden.has(c.id)).map((c) => ({ kind: 'crew', id: c.id, by: c.by, text: c.text, from: c.from, exercise: c.exercise, posted: c.kind, day: c.day, at: startedAt + Math.max(0, c.day - 1) * 86_400_000 - (c.day === 0 ? 120_000 : 60_000) + c.order * 1000 }));
    return [...mine, ...theirs, ...crew].sort((a, b) => b.at - a.at);
  }, [posts, friendPosts, hidden, profile.madeDays, profile.met, profile.startedAt, now]);

  const dismissSave = () => { const p = { ...profile, saveDismissed: true }; saveProfile(p); setProfile(p); track('account_save_dismissed'); };
  // A sign-in means this device now belongs to a feed made elsewhere: boot again so the road comes from the server (16 §5).
  const onGoogle = async () => { setSaving(true); const r = await saveWithGoogle(); setSaving(false); if (r === 'saved') { show('Saved. Your feed is yours.'); track('account_saved', { via: 'google' }); setProfile(loadProfile()); } else if (r === 'signed_in') { show('Welcome back. Opening your feed…'); setTimeout(() => window.location.replace('/'), 900); } else if (r === 'failed') show('That did not work. Try email.'); };
  const onEmail = async () => { if (!email.includes('@')) return; setSaving(true); const r = await saveWithEmail(email.trim()); setSaving(false); if (r === 'email_sent') { setEmailSent(true); track('account_save_link_sent'); } else show('That did not send. Try again later.'); };

  const onHeart = async (p: FeedPost) => { try { const on = await toggleHeart(p); track('heart_given', { on }); reload(); } catch { show('Could not like that.'); } };
  const onDelete = async (p: FeedPost) => { if (!window.confirm('Delete this post? It will disappear from your feed and from anyone you sent it to.')) return; try { await deletePost(p.postId); track('post_deleted'); reload(); } catch { show('Could not delete it.'); } };
  const onReport = async (id: string) => { try { await reportPost(id); track('post_reported'); setHidden(hiddenPostIds()); show('Reported and hidden.'); } catch { show('Could not report it.'); } };
  // The label used to be the state alone — a button reading "Friends", which
  // says nothing about what tapping it does, and in fact did the opposite of
  // what it looked like (interview 2026-09-18). Now it says where the thing
  // stands and what the tap would change it to, and the toast confirms it
  // landed, because nothing else on the card moves.
  const onVisibility = async (p: FeedPost) => {
    const v = p.visibility === 'me' ? 'friends' : 'me';
    try { await setVisibility(p.postId, v); show(v === 'me' ? 'Only you can see it now.' : 'Your friends can see it now.'); reload(); }
    catch { show('Could not change that.'); }
  };

  return (
    <Frame tabs left={<div className="row" style={{ gap: 8 }}><Avatar who="me" size={30} name={profile.name} /><span className="tag">{posts ? `${posts.length} ${posts.length === 1 ? 'post' : 'posts'}` : '…'} · Day {profile.madeDays || day}</span></div>}>
      {(friends.length > 0 || crewMet(profile.met).length > 0) && (
        <div className="crew-row">
          {friends.map((fr) => <div key={fr.uid} className="crew-face"><span className="story-ring"><Avatar who="me" size={54} name={fr.name || '?'} /></span><span className="hint">{fr.name || 'Friend'}</span></div>)}
          {crewMet(profile.met).map((id) => (
            <div key={id} className="crew-face"><span className="story-ring"><Avatar who={id} size={54} /></span><span className="hint">{CREW[id].name}</span></div>
          ))}
        </div>
      )}

      {knock && !knockLater && (
        <div className="knock">
          <div className="row" style={{ gap: 10 }}>
            <Avatar who={knock} size={44} />
            <div className="stack" style={{ gap: 0 }}><span className="label" style={{ color: 'var(--butter-dark)' }}>Someone at the door</span><b>{CREW[knock].name} · {CREW[knock].part}</b></div>
          </div>
          <p className="why">{INTROS[knock].text}</p>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn small butter" style={{ flex: 1 }} onClick={() => letIn(knock)}>Hello</button>
            <button className="link" onClick={() => { setKnockLater(true); track('crew_knock_later', { crew: knock }); }}>Not now</button>
          </div>
          <span className="hint">Until you say hello, nothing of theirs is on your feed.</span>
        </div>
      )}

      {/* A find can land while you are on the feed, so it is offered here too. */}
      <CrewFinds />

      {posts === null && <p className="hint">Opening your feed…</p>}
      {items.map((it) => it.kind === 'mine' ? (
        <Post key={it.post.postId} who="me" name={profile.name || 'You'}
          sub={<>Day {it.post.day} · {postTitle(it.post)}{it.post.visibility === 'me' ? ' · only you' : ''}</>}
          slides={[
            ...(it.held > 0 ? [{ kind: 'wait', dark: true, text: it.held > 1 ? `${it.held} prints from your crew are dry.` : 'A print from your crew is dry.', cta: it.held > 1 ? 'Turn them over ›' : 'Turn it over ›', onOpen: () => { track('print_open_from', { from: 'feed' }); nav(`/print/${it.post.postId}`); } } as Slide] : []),
            ...postSlides(it.post, 'your', open),
          ]}
          hearted={it.post.hearts.length > 0} onHeart={() => onHeart(it.post)} audioUrl={it.post.audioUrl}
          menu={it.post.kind === 'film' || it.post.kind === 'character' ? [{ label: 'Delete', danger: true, onClick: () => onDelete(it.post) }] : [
            { label: it.post.visibility === 'me' ? 'Only you can see this · show friends' : 'Friends can see this · make private', onClick: () => onVisibility(it.post) },
            { label: 'Delete', danger: true, onClick: () => onDelete(it.post) },
          ]} />
      ) : it.kind === 'friend' ? (
        <Post key={it.post.postId} who="me" name={it.post.byName || 'A friend'}
          sub={<>Day {it.post.day} · {postTitle(it.post)}</>}
          slides={postSlides(it.post, it.post.byName ? `${it.post.byName}’s` : 'their', open)}
          hearted={it.post.hearts.length > 0} onHeart={() => onHeart(it.post)} audioUrl={it.post.audioUrl}
          menu={[{ label: 'Report', danger: true, onClick: () => onReport(it.post.postId) }]} />
      ) : (
        <Post key={it.id} className="crew" who={it.by} name={CREW[it.by].name}
          sub={<>{it.day === 0 ? 'Day 1' : `Day ${it.day}`} · {it.posted === 'intro' ? 'says hello' : CREW[it.by].part.toLowerCase()}</>}
          slides={[crewSlide(it)]}
          menu={[{ label: 'Report', danger: true, onClick: () => onReport(it.id) }]} />
      ))}

      {/* Under your posts, not over them. At the top of the feed, "Save your feed?"
          arrived before anyone knew what the feed was, and read as out of place
          (interview 3, 2026-09-20). Down here it follows the things it keeps. */}
      {showSave && (
        <div className="sheet" style={{ gap: 10 }}>
          {emailSent ? (<>
            <span className="label">Check your email</span>
            {iab
              ? <p className="why">The link in it opens your feed in your phone's own browser, and keeps it there. Until then, nothing has changed.</p>
              : <p className="why">The link in it opens your feed here, and keeps it. Until then, nothing has changed.</p>}
            {/* After the link has gone: carrying on in the phone's own browser now, rather than from the email */}
            {iab && <MoveToBrowser from="feed" />}
            <button className="link" onClick={() => setEmailSent(false)}>Use a different email</button>
          </>) : (<>
            <span className="label">Keep your work?</span>
            {iab
              ? <p className="why">Everything you've made lives only in {inAppName(iab)}'s browser for now. Add your email and it's kept on any phone, in any browser. It's also how reminders reach you.</p>
              : <p className="why">Everything you've made lives only on this phone for now. Add Google or an email and it's kept if you switch phones. It's also how reminders reach you.</p>}
            {!iab && <button className="btn small" onClick={onGoogle} disabled={saving} style={{ width: '100%' }}>Keep it with Google</button>}
            <div className="row">
              <input className="input" type="email" inputMode="email" placeholder={iab ? 'your email' : 'or your email'} value={email} onChange={(e) => setEmail(e.target.value)} />
              <button className="btn small" onClick={onEmail} disabled={saving || !email.includes('@')}>Send link</button>
            </div>
            <button className="link" onClick={dismissSave}>Not now</button>
          </>)}
        </div>
      )}
      {posts && posts.length === 0 && (
        <div className="sheet" style={{ alignItems: 'center', textAlign: 'center' }}>
          <p className="why">Nothing of yours here yet.</p>
          <button className="btn small" onClick={() => nav('/')}>Today’s exercise</button>
        </div>
      )}
      {lightbox}
      {toast}
    </Frame>
  );
}

function postTitle(p: FeedPost): string {
  return p.kind === 'character' ? 'your crew' : p.kind === 'film' ? 'first film' : getChallenge(p.challengeId)?.title ?? p.challengeId;
}

/** Everything that was typed, one idea to a line, for the unfolded caption. */
function allWords(w: Work | undefined): string[] {
  if (!w) return [];
  const s = (v: unknown) => String(v ?? '').replace(/\s+/g, ' ').trim();
  return [
    ...(w.lines || []).map(s),
    ...Object.values(w.fields || {}).map(s),
    ...(w.pairs || []).map((p) => [s(p.name), s(p.sub)].filter(Boolean).join(' — ')),
    s(w.text),
    (w.emoji || []).join(' '),
  ].filter(Boolean);
}

/**
 * A post's pictures, biggest first. The crew's drawings lead a written piece —
 * they are the picture — and the card with the words on it follows; a photo
 * piece leads with its own photograph. A film is its clip, a character its portrait.
 */
function postSlides(p: FeedPost, whose: string, open: (url: string, caption?: string) => void): Slide[] {
  if (p.kind === 'invite') return [];
  if (p.kind === 'film') return p.videoUrl
    ? [{ kind: 'video', url: p.videoUrl, poster: p.cardUrl || undefined, pill: 'Rolled by the crew', caption: 'A first film', more: `Made from ${whose} own pieces.` }]
    : [{ kind: 'wait', text: p.filmStatus === 'failed' ? 'The clip did not come out.' : 'The crew are rolling it…' }];
  if (p.kind === 'character') return [{ kind: 'image', url: p.cardUrl, pill: 'Drawn by the crew', caption: <><b>{p.characterName || 'Not named yet'}</b> · {p.headline}</>, more: `From ${whose} Day ${p.day} line.`, onOpen: () => open(p.cardUrl, p.headline) }];
  const words = allWords(p.work);
  const title = getChallenge(p.challengeId)?.title ?? p.headline;
  const card: Slide = { kind: 'image', fit: 'sheet', url: p.cardUrl, pill: '✎ By hand', caption: <b>{title}</b>, more: words.length ? <>{words.map((l, i) => <span className="cap-para" key={i}>{l}</span>)}</> : undefined, onOpen: () => open(p.cardUrl, p.headline) };
  const drawn: Slide[] = (p.renders || []).map((r) => ({ kind: 'image', url: r.url, pill: 'Drawn by the crew', caption: `“${r.idea}”`, more: <>From {whose} Day {p.day} ideas · {title}</>, onOpen: () => open(r.url, `“${r.idea}” · drawn by the crew`) }));
  // "Who are you?" is the picture of you once it is drawn; the name tag it came from is not the post (owner, 2026-09-30)
  if (getChallenge(p.challengeId)?.input.kind === 'whoami' && drawn.length) return drawn.map((s) => ({ ...s, more: <>From {whose} work · {title}</> }));
  return p.kind === 'photo' ? [card, ...drawn] : [...drawn, card];
}

/** A crew post is words; the day Kraben deals is shown as that exercise's own picture, with his words on it. */
function crewSlide(it: Extract<Item, { kind: 'crew' }>): Slide {
  const ex = it.exercise ? getChallenge(it.exercise) : undefined;
  if (ex) return { kind: 'image', url: `/art/exercises/${ex.id}.jpg`, pill: `Day ${it.day + 1} · dealt`, caption: <b>{ex.title}</b>, more: ex.encouragement || undefined };
  return { kind: 'words', colour: CREW[it.by].colour, portrait: it.by === 'kraben' ? undefined : `/art/crew/${it.by}.jpg`, pill: 'Crew', text: it.posted === 'quote' ? `“${it.text}”` : it.text, caption: it.from ? <>— {it.from}</> : undefined };
}
